import React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    TextField,
} from "@mui/material";
import { GetServerSideProps } from "next";
import config from "../../../../config";
import { EcommerceEventList } from "../../../../models/Event";
import PageWrapper from "../../../../components/PageWrapper";
import Typography from "@mui/material/Typography";
import {
    getErrorMessage,
    setupStyleMapFromStyleMap,
} from "../../../../utils/helpers";
import toast from "../../../../utils/toast";
import { useRouter } from "next/router";
import SpecialDepartmentEndpoints from "../../../../api/SpecialDepartmentEndpoints";
import Head from "next/head";

type Props = {
    eventList: EcommerceEventList;
};

export default function CorporateLogin({ eventList }: Props) {
    const [code, setCode] = React.useState("");
    const [dismissed, setDismissed] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const router = useRouter();

    function onDismiss() {
        setDismissed(true);
    }

    function onChangeCode(e: React.ChangeEvent<HTMLInputElement>) {
        setCode(e.target.value);
    }

    async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        setLoading(true);

        try {
            // * Check if SDA code is valid, then redirect to SDA page
            await SpecialDepartmentEndpoints.getEventList(code);
            router.push(`/store/special/${code}`);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if (eventList.newStyleMap) {
            setupStyleMapFromStyleMap(eventList.newStyleMap);
        }
    }, []);

    // TODO: Duplicate this page to /vendor-login for 2024
    const isCneVendor = eventList.clientId === 74;

    let pageTitle = `${eventList.clientName} Corporate Login | AuthentiGATE`;
    if (isCneVendor) {
        pageTitle = "CNE Vendor Login | AuthentiGATE";
    }

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="robots" content="noindex" />
            </Head>
            <PageWrapper eventList={eventList} noCart>
                <Container maxWidth="md">
                    <Grid container spacing={2}>
                        {!dismissed && !isCneVendor && (
                            <Grid item xs={12}>
                                <Alert severity="info" onClose={onDismiss}>
                                    Registration and approval is required to
                                    access this ticketing section.
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <form onSubmit={onSubmit}>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="h4"
                                                    color="primaryText.main"
                                                    textAlign="center"
                                                >
                                                    Enter Your Access Code
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                {!isCneVendor ? (
                                                    <Typography
                                                        variant="h6"
                                                        color="primaryText.main"
                                                        textAlign="center"
                                                    >
                                                        Please enter your
                                                        corporate access code
                                                        below to access your
                                                        discounted tickets
                                                    </Typography>
                                                ) : (
                                                    <Typography
                                                        variant="h6"
                                                        color="primaryText.main"
                                                        textAlign="center"
                                                    >
                                                        Please enter your vendor
                                                        access code below to
                                                        purchase admission and
                                                        parking vouchers
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="standard"
                                                    label="Access Code"
                                                    placeholder="ABCD1234"
                                                    value={code}
                                                    onChange={onChangeCode}
                                                    disabled={loading}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    fullWidth
                                                    disabled={loading || !code}
                                                    variant="contained"
                                                    size="small"
                                                >
                                                    Login
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </form>
                        </Grid>
                    </Grid>
                </Container>
            </PageWrapper>
        </>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const cartUrl = context.params?.cartUrl;
    let data: any,
        error = false;

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/cart-style/${cartUrl}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": "ECOMMERCE",
                },
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(await res.text());
        } else {
            data = await res.json();
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    if (data?.newStyleMap) {
        setupStyleMapFromStyleMap(data.newStyleMap);
    }

    return {
        props: {
            eventList: data,
        },
        notFound: error,
    };
};
